import { useCallback, useEffect, useState } from "react";
import { ChevronDown, ChevronUp, Eye, EyeOff, Pencil, Plus, Trash2 } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import {
    Dialog,
    DialogContent,
    DialogDescription,
    DialogHeader,
    DialogTitle,
} from "@/components/ui/dialog";
import {
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableHeader,
    TableRow,
} from "@/components/ui/table";
import api, { formatApiError } from "@/lib/api";

const CATS = [
    { id: "kiloan", label: "Kiloan (per kg)" },
    { id: "satuan", label: "Satuan (per pcs)" },
    { id: "sepatu", label: "Sepatu & Sandal" },
];

const EMPTY_FORM = { name: "", price: "", ket: "", eta: "" };

export default function PricingPanel({ toast }) {
    const [items, setItems] = useState([]);
    const [loading, setLoading] = useState(true);
    const [cat, setCat] = useState("kiloan");
    const [dialog, setDialog] = useState(null);
    const [saving, setSaving] = useState(false);

    const load = useCallback(async () => {
        try {
            const res = await api.get("/admin/pricing");
            setItems(res.data);
        } catch (err) {
            toast({
                title: "Gagal memuat daftar harga",
                description: formatApiError(err),
                variant: "destructive",
            });
        } finally {
            setLoading(false);
        }
    }, [toast]);

    useEffect(() => {
        load();
    }, [load]);

    const catItems = items.filter((i) => i.category === cat);
    const rows = catItems.filter((i) => !i.is_extra);
    const extras = catItems.filter((i) => i.is_extra);
    const catLabel = CATS.find((c) => c.id === cat)?.label || cat;

    const openAdd = (isExtra) =>
        setDialog({ item: null, is_extra: isExtra, form: { ...EMPTY_FORM } });

    const openEdit = (item) =>
        setDialog({
            item,
            is_extra: item.is_extra,
            form: {
                name: item.name,
                price: item.price,
                ket: item.ket,
                eta: item.eta,
            },
        });

    const save = async () => {
        const { name, price, ket, eta } = dialog.form;
        if (!name.trim() || (!dialog.is_extra && !price.trim())) {
            toast({
                title: "Lengkapi data",
                description: dialog.is_extra
                    ? "Isi keterangan biaya tambahan."
                    : "Nama layanan dan harga wajib diisi.",
                variant: "destructive",
            });
            return;
        }
        setSaving(true);
        try {
            if (dialog.item) {
                const payload = { name: name.trim() };
                if (!dialog.is_extra) {
                    payload.price = price.trim();
                    payload.ket = ket.trim();
                    payload.eta = eta.trim();
                }
                await api.patch(`/admin/pricing/${dialog.item.id}`, payload);
            } else {
                const payload = { category: cat, name: name.trim() };
                if (dialog.is_extra) {
                    payload.is_extra = true;
                } else {
                    payload.price = price.trim();
                    payload.ket = ket.trim();
                    payload.eta = eta.trim();
                }
                await api.post("/admin/pricing", payload);
            }
            toast({
                title: dialog.item ? "Perubahan tersimpan" : "Item baru ditambahkan",
            });
            setDialog(null);
            load();
        } catch (err) {
            toast({
                title: "Gagal menyimpan",
                description: formatApiError(err),
                variant: "destructive",
            });
        } finally {
            setSaving(false);
        }
    };

    const remove = async (item) => {
        if (!window.confirm(`Hapus "${item.name}" dari daftar harga?`)) return;
        try {
            await api.delete(`/admin/pricing/${item.id}`);
            toast({ title: "Item dihapus" });
            load();
        } catch (err) {
            toast({
                title: "Gagal menghapus",
                description: formatApiError(err),
                variant: "destructive",
            });
        }
    };

    const move = async (item, direction) => {
        try {
            const res = await api.post(`/admin/pricing/${item.id}/move`, {
                direction,
            });
            if (res.data?.moved) load();
        } catch (err) {
            toast({
                title: "Gagal mengurutkan",
                description: formatApiError(err),
                variant: "destructive",
            });
        }
    };

    const toggleActive = async (item) => {
        try {
            await api.patch(`/admin/pricing/${item.id}`, {
                is_active: !item.is_active,
            });
            toast({
                title: item.is_active
                    ? "Item disembunyikan dari website"
                    : "Item ditampilkan kembali",
            });
            load();
        } catch (err) {
            toast({
                title: "Gagal mengubah status",
                description: formatApiError(err),
                variant: "destructive",
            });
        }
    };

    return (
        <div data-testid="pricing-panel">
            <div className="flex flex-wrap items-center justify-between gap-3">
                <p className="max-w-md text-sm text-ink/50">
                    Ubah harga & layanan — perubahan langsung tampil di tabel
                    harga pada website.
                </p>
                <div className="flex flex-wrap gap-2">
                    <Button
                        data-testid="pricing-add-row-button"
                        size="sm"
                        onClick={() => openAdd(false)}
                        className="rounded-full bg-brand font-bold text-white hover:bg-brand-deep"
                    >
                        <Plus size={15} className="mr-1" />
                        Tambah Layanan
                    </Button>
                    <Button
                        data-testid="pricing-add-extra-button"
                        size="sm"
                        variant="outline"
                        onClick={() => openAdd(true)}
                        className="rounded-full border-ink/10 font-bold text-ink hover:bg-brand-mist/50 hover:text-brand"
                    >
                        <Plus size={15} className="mr-1" />
                        Biaya Tambahan
                    </Button>
                </div>
            </div>

            <div
                data-testid="pricing-admin-tabs"
                className="mt-5 inline-flex flex-wrap gap-1.5 rounded-full border border-border bg-white p-1.5 shadow-brand"
            >
                {CATS.map((c) => (
                    <button
                        key={c.id}
                        data-testid={`pricing-admin-tab-${c.id}`}
                        onClick={() => setCat(c.id)}
                        className={`rounded-full px-4 py-2 text-sm font-bold transition-all duration-200 ${
                            cat === c.id
                                ? "bg-brand text-white shadow-brand"
                                : "text-ink/60 hover:text-brand"
                        }`}
                    >
                        {c.label}
                    </button>
                ))}
            </div>

            <div className="mt-5 overflow-hidden rounded-3xl border border-border bg-white shadow-brand">
                <div className="overflow-x-auto" data-testid="pricing-admin-table">
                    {loading ? (
                        <p className="py-14 text-center text-sm text-ink/50">
                            Memuat...
                        </p>
                    ) : (
                        <Table className="min-w-[760px]">
                            <TableHeader>
                                <TableRow className="bg-brand-mist/60 hover:bg-brand-mist/60">
                                    <TableHead className="font-bold text-ink/60">Layanan</TableHead>
                                    <TableHead className="font-bold text-ink/60">Harga</TableHead>
                                    <TableHead className="font-bold text-ink/60">Ketentuan</TableHead>
                                    <TableHead className="font-bold text-ink/60">Estimasi</TableHead>
                                    <TableHead className="font-bold text-ink/60">Aksi</TableHead>
                                </TableRow>
                            </TableHeader>
                            <TableBody>
                                {rows.map((r, idx) => (
                                    <TableRow
                                        key={r.id}
                                        data-testid={`pricing-admin-row-${r.id}`}
                                        className="transition-colors hover:bg-brand-mist/40"
                                    >
                                        <TableCell
                                            className={`font-semibold ${
                                                r.is_active
                                                    ? "text-ink"
                                                    : "text-ink/30 line-through"
                                            }`}
                                        >
                                            {r.name}
                                        </TableCell>
                                        <TableCell className="whitespace-nowrap font-display text-base font-extrabold text-brand">
                                            {r.price}
                                        </TableCell>
                                        <TableCell className="whitespace-nowrap text-sm text-ink/50">
                                            {r.ket}
                                        </TableCell>
                                        <TableCell className="whitespace-nowrap text-sm text-ink/50">
                                            {r.eta}
                                        </TableCell>
                                        <TableCell>
                                            <div className="flex gap-1">
                                                <Button
                                                    data-testid={`pricing-up-${r.id}`}
                                                    size="sm"
                                                    variant="ghost"
                                                    onClick={() => move(r, "up")}
                                                    disabled={idx === 0}
                                                    aria-label="Naikkan urutan"
                                                    className="h-8 w-8 p-0 text-ink/40 hover:text-brand disabled:opacity-20"
                                                >
                                                    <ChevronUp size={15} />
                                                </Button>
                                                <Button
                                                    data-testid={`pricing-down-${r.id}`}
                                                    size="sm"
                                                    variant="ghost"
                                                    onClick={() => move(r, "down")}
                                                    disabled={idx === rows.length - 1}
                                                    aria-label="Turunkan urutan"
                                                    className="h-8 w-8 p-0 text-ink/40 hover:text-brand disabled:opacity-20"
                                                >
                                                    <ChevronDown size={15} />
                                                </Button>
                                                <Button
                                                    data-testid={`pricing-toggle-${r.id}`}
                                                    size="sm"
                                                    variant="ghost"
                                                    onClick={() => toggleActive(r)}
                                                    aria-label={r.is_active ? "Sembunyikan dari website" : "Tampilkan di website"}
                                                    className="h-8 w-8 p-0 text-ink/40 hover:text-brand"
                                                >
                                                    {r.is_active ? <Eye size={15} /> : <EyeOff size={15} />}
                                                </Button>
                                                <Button
                                                    data-testid={`pricing-edit-${r.id}`}
                                                    size="sm"
                                                    variant="ghost"
                                                    onClick={() => openEdit(r)}
                                                    aria-label="Ubah item"
                                                    className="h-8 w-8 p-0 text-ink/40 hover:text-brand"
                                                >
                                                    <Pencil size={15} />
                                                </Button>
                                                <Button
                                                    data-testid={`pricing-delete-${r.id}`}
                                                    size="sm"
                                                    variant="ghost"
                                                    onClick={() => remove(r)}
                                                    aria-label="Hapus item"
                                                    className="h-8 w-8 p-0 text-ink/40 hover:text-red-600"
                                                >
                                                    <Trash2 size={15} />
                                                </Button>
                                            </div>
                                        </TableCell>
                                    </TableRow>
                                ))}
                                {rows.length === 0 && (
                                    <TableRow>
                                        <TableCell colSpan={5} className="py-10 text-center text-sm text-ink/50">
                                            Belum ada layanan di kategori ini —
                                            tambahkan lewat tombol "Tambah Layanan".
                                        </TableCell>
                                    </TableRow>
                                )}
                            </TableBody>
                        </Table>
                    )}
                </div>

                {extras.length > 0 && (
                    <div
                        className="border-t border-border bg-[#F9FAFB] px-6 py-5 sm:px-8"
                        data-testid="pricing-admin-extras"
                    >
                        <p className="text-xs font-bold uppercase tracking-widest text-ink/40">
                            Layanan Tambahan
                        </p>
                        <div className="mt-3 flex flex-wrap gap-2">
                            {extras.map((e, idx) => (
                                <span
                                    key={e.id}
                                    className={`inline-flex items-center gap-1.5 rounded-full bg-white px-3.5 py-1.5 text-xs font-semibold shadow-sm ring-1 ring-border ${
                                        e.is_active ? "text-ink/60" : "text-ink/30 line-through"
                                    }`}
                                >
                                    <button
                                        data-testid={`pricing-extra-up-${e.id}`}
                                        onClick={() => move(e, "up")}
                                        disabled={idx === 0}
                                        aria-label="Naikkan urutan"
                                        className="text-ink/30 transition-colors hover:text-brand disabled:opacity-20"
                                    >
                                        <ChevronUp size={12} />
                                    </button>
                                    <button
                                        data-testid={`pricing-extra-down-${e.id}`}
                                        onClick={() => move(e, "down")}
                                        disabled={idx === extras.length - 1}
                                        aria-label="Turunkan urutan"
                                        className="text-ink/30 transition-colors hover:text-brand disabled:opacity-20"
                                    >
                                        <ChevronDown size={12} />
                                    </button>
                                    {e.name}
                                    <button
                                        data-testid={`pricing-edit-${e.id}`}
                                        onClick={() => openEdit(e)}
                                        aria-label="Ubah biaya tambahan"
                                        className="text-ink/30 transition-colors hover:text-brand"
                                    >
                                        <Pencil size={12} />
                                    </button>
                                    <button
                                        data-testid={`pricing-delete-${e.id}`}
                                        onClick={() => remove(e)}
                                        aria-label="Hapus biaya tambahan"
                                        className="text-ink/30 transition-colors hover:text-red-600"
                                    >
                                        <Trash2 size={12} />
                                    </button>
                                </span>
                            ))}
                        </div>
                    </div>
                )}
            </div>

            <Dialog open={!!dialog} onOpenChange={(o) => !o && setDialog(null)}>
                <DialogContent data-testid="pricing-dialog" className="rounded-3xl sm:max-w-md">
                    <DialogHeader>
                        <DialogTitle className="font-display text-xl font-extrabold text-ink">
                            {dialog?.item
                                ? "Ubah Item"
                                : dialog?.is_extra
                                    ? "Tambah Biaya Tambahan"
                                    : "Tambah Layanan"}
                            {" — "}
                            {catLabel}
                        </DialogTitle>
                        <DialogDescription className="text-sm text-ink/50">
                            {dialog?.is_extra
                                ? "Biaya tambahan tampil sebagai chip di bawah tabel harga."
                                : "Baris ini langsung tampil di tabel harga pada website."}
                        </DialogDescription>
                    </DialogHeader>
                    <div className="space-y-4">
                        <div className="space-y-2">
                            <Label className="text-sm font-bold text-ink/80">
                                {dialog?.is_extra
                                    ? "Keterangan (mis. Hanger — Rp 2.000)"
                                    : "Nama Layanan"}
                            </Label>
                            <Input
                                data-testid="pricing-name-input"
                                value={dialog?.form.name || ""}
                                onChange={(e) =>
                                    setDialog((d) => ({
                                        ...d,
                                        form: { ...d.form, name: e.target.value },
                                    }))
                                }
                                className="h-12 rounded-xl border-ink/10 bg-[#F9FAFB] focus-visible:ring-brand"
                            />
                        </div>
                        {!dialog?.is_extra && (
                            <>
                                <div className="grid grid-cols-2 gap-4">
                                    <div className="space-y-2">
                                        <Label className="text-sm font-bold text-ink/80">Harga</Label>
                                        <Input
                                            data-testid="pricing-price-input"
                                            placeholder="Rp 6.500"
                                            value={dialog?.form.price || ""}
                                            onChange={(e) =>
                                                setDialog((d) => ({
                                                    ...d,
                                                    form: { ...d.form, price: e.target.value },
                                                }))
                                            }
                                            className="h-12 rounded-xl border-ink/10 bg-[#F9FAFB] focus-visible:ring-brand"
                                        />
                                    </div>
                                    <div className="space-y-2">
                                        <Label className="text-sm font-bold text-ink/80">Ketentuan</Label>
                                        <Input
                                            data-testid="pricing-ket-input"
                                            placeholder="Min. 3 kg"
                                            value={dialog?.form.ket || ""}
                                            onChange={(e) =>
                                                setDialog((d) => ({
                                                    ...d,
                                                    form: { ...d.form, ket: e.target.value },
                                                }))
                                            }
                                            className="h-12 rounded-xl border-ink/10 bg-[#F9FAFB] focus-visible:ring-brand"
                                        />
                                    </div>
                                </div>
                                <div className="space-y-2">
                                    <Label className="text-sm font-bold text-ink/80">Estimasi</Label>
                                    <Input
                                        data-testid="pricing-eta-input"
                                        placeholder="3 hari"
                                        value={dialog?.form.eta || ""}
                                        onChange={(e) =>
                                            setDialog((d) => ({
                                                ...d,
                                                form: { ...d.form, eta: e.target.value },
                                            }))
                                        }
                                        className="h-12 rounded-xl border-ink/10 bg-[#F9FAFB] focus-visible:ring-brand"
                                    />
                                </div>
                            </>
                        )}
                    </div>
                    <div className="flex gap-3 pt-2">
                        <Button
                            data-testid="pricing-save"
                            onClick={save}
                            disabled={saving}
                            className="h-11 flex-1 rounded-full bg-brand font-bold text-white hover:bg-brand-deep disabled:opacity-60"
                        >
                            {saving ? "Menyimpan..." : "Simpan"}
                        </Button>
                        <Button
                            variant="outline"
                            onClick={() => setDialog(null)}
                            className="h-11 rounded-full border-ink/10 font-bold text-ink hover:bg-brand-mist/50 hover:text-brand"
                        >
                            Batal
                        </Button>
                    </div>
                </DialogContent>
            </Dialog>
        </div>
    );
}
